/**
 * Ask AI: the pure logic behind the chat window and its settings panel.
 *
 * The browser keeps the conversation and sends it with every question; the server checks it,
 * answers, and keeps nothing between questions. Only the turns that were actually answered are
 * sent back, so a failed question does not leave a dangling turn in the history.
 */

export type AiModelId = 'claude-haiku-4-5' | 'claude-sonnet-5-5';

export interface AiModelOption {
    id: AiModelId;
    label: string;
}

export interface AiAssistantStatus {
    enabled: boolean;
    configured: boolean;
    defaultModel: AiModelId;
    models: AiModelOption[];
}

export type AiAnswerOutcome = 'answered' | 'refused' | 'incomplete' | 'failed';

export interface AiAskResult {
    answer: string;
    outcome: AiAnswerOutcome;
    model: AiModelId;
    toolCalls: number;
    costUsd: number;
    monthToDateCostUsd: number;
    budgetUsd: number;
}

export type AiChatMessage =
    | { id: string; kind: 'question'; text: string }
    | {
          id: string;
          kind: 'answer';
          text: string;
          outcome: AiAnswerOutcome;
          model: AiModelId;
          costUsd: number;
      }
    | { id: string; kind: 'error'; errorKey: string };

export interface AiHistoryEntry {
    role: 'user' | 'assistant';
    content: string;
}

/** The server keeps the 19 most recent turns; there is no point sending more. */
export const MAX_SENT_HISTORY = 19;
export const MAX_QUESTION_LENGTH = 4000;

/**
 * The answered turns of the conversation followed by the new question: question/answer pairs
 * whose answer has text, in order. Unanswered questions and errors are left out.
 */
export const buildAskHistory = (messages: readonly AiChatMessage[], question: string): AiHistoryEntry[] => {
    const history: AiHistoryEntry[] = [];
    messages.forEach((message, index) => {
        const next = messages[index + 1];
        if (message.kind === 'question' && next?.kind === 'answer' && next.text.trim() !== '') {
            history.push(
                { role: 'user', content: message.text },
                { role: 'assistant', content: next.text },
            );
        }
    });
    history.push({ role: 'user', content: question.trim() });
    return history.slice(-MAX_SENT_HISTORY);
};

/** The model to preselect: the one used last if still offered, otherwise the default. */
export const resolveSelectedModel = (
    status: Pick<AiAssistantStatus, 'defaultModel' | 'models'>,
    remembered: string | null,
): AiModelId | null => {
    const offered = status.models.map(model => model.id);
    const rememberedModel = offered.find(id => id === remembered);
    if (rememberedModel !== undefined) {
        return rememberedModel;
    }
    return offered.includes(status.defaultModel) ? status.defaultModel : (offered[0] ?? null);
};

export const canSendQuestion = (question: string, busy: boolean): boolean => {
    const length = question.trim().length;
    return !busy && length > 0 && length <= MAX_QUESTION_LENGTH;
};

const KNOWN_ERROR_CODES = new Set([
    'INVALID_CONVERSATION',
    'QUESTION_TOO_LONG',
    'INVALID_SETTINGS',
    'API_KEY_REQUIRED',
    'ENCRYPTION_UNAVAILABLE',
    'DISABLED',
    'NOT_CONFIGURED',
    'MODEL_NOT_ALLOWED',
    'BUDGET_EXCEEDED',
    'PROVIDER_AUTH',
    'PROVIDER_RATE_LIMIT',
    'PROVIDER_UNAVAILABLE',
    'PROVIDER_ERROR',
]);

/**
 * The translation key for a failed request. The API answers with a bare code, which the
 * request helper turns into the error's message.
 */
export const aiErrorKey = (error: unknown): string => {
    const message = error instanceof Error ? error.message.trim() : '';
    return KNOWN_ERROR_CODES.has(message)
        ? `aiAssistant.errors.${message}`
        : 'aiAssistant.errors.GENERIC';
};

/** The note shown with an answer that is not a complete one; null for a normal answer. */
export const answerNoticeKey = (outcome: AiAnswerOutcome): string | null => {
    if (outcome === 'refused') {
        return 'aiAssistant.notices.refused';
    }
    return outcome === 'incomplete' ? 'aiAssistant.notices.incomplete' : null;
};

/** Dollars with four decimals below one dollar, where AI costs usually are, two above. */
export const formatUsd = (value: number): string =>
    `$${value.toFixed(Math.abs(value) < 1 ? 4 : 2)}`;

/** Share of the monthly budget already spent, 0-100; null when there is no budget. */
export const budgetUsedPercent = (spentUsd: number, budgetUsd: number): number | null =>
    budgetUsd > 0 ? Math.min(100, Math.round((spentUsd / budgetUsd) * 100)) : null;

/** How far from the right edge the Ask AI button sits: left of the Sumator when it is open. */
export const SUMATOR_WIDTH = 320;
export const askAiButtonRight = (sumatorOpen: boolean): number =>
    sumatorOpen ? 24 + SUMATOR_WIDTH + 16 : 24;

/** Raised on `window` after the settings are saved, so the Ask AI button refreshes itself. */
export const AI_SETTINGS_CHANGED_EVENT = 'ask-ai-settings-changed';

export interface AiSettingsView {
    settings: Omit<AiSettingsForm, 'apiKey'>;
    apiKey: { configured: boolean; readable: boolean; masked: string | null };
    encryptionAvailable: boolean;
    updatedAt: string | null;
    catalog: { id: AiModelId; label: string }[];
    usage: {
        monthToDate: { requests: number; inputTokens: number; outputTokens: number; costUsd: number };
        budgetUsd: number;
    };
}

export interface AiModelSettingForm {
    id: AiModelId;
    enabled: boolean;
    inputPerMillion: number;
    outputPerMillion: number;
}

export interface AiSettingsForm {
    enabled: boolean;
    apiKey?: string;
    models: AiModelSettingForm[];
    defaultModel: AiModelId;
    monthlyBudgetUsd: number;
    maxOutputTokens: number;
}

/**
 * The body of a settings save. An empty key field keeps the stored key, so the key is only
 * sent when the administrator typed a new one.
 */
export const toSettingsPayload = (form: AiSettingsForm, clearApiKey: boolean): Record<string, unknown> => {
    const apiKey = form.apiKey?.trim() ?? '';
    return {
        enabled: form.enabled,
        models: form.models.map(model => ({
            id: model.id,
            enabled: model.enabled,
            inputPerMillion: Number(model.inputPerMillion),
            outputPerMillion: Number(model.outputPerMillion),
        })),
        defaultModel: form.defaultModel,
        monthlyBudgetUsd: Number(form.monthlyBudgetUsd),
        maxOutputTokens: Number(form.maxOutputTokens),
        ...(apiKey === '' ? {} : { apiKey }),
        ...(clearApiKey ? { clearApiKey: true } : {}),
    };
};
